import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import useClassesStore from '../../../stateManagers/store/useClassesStore';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalDateField } from '../../../components/GlobalComponents/GlobalDateField';
import { DropdownOption, GlobalDropdownField } from '../../../components/GlobalComponents/GlobalDropdownField';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { RadioOption, GlobalRadioButtons } from '../../../components/GlobalComponents/GlobalRadioButtons';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

interface StudentFormData {
  name: string;
  class: string; // holds class_id (classOptions already key by id)
  section: string | null;
  gender: string;
  email_id: string;
  admission_no: string;
  roll_no: string;
  dob: Date | null;
  blood_group: string;
  father_name: string;
  mother_name: string;
  father_no: string;
  mother_no: string;
  address: string;
  user_login: string;
  user_pass: string;
  admission_type: string | null;
}

interface RegisterStudentFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (shouldResetPage?: boolean) => void;
  studentData?: any | null;
}

const RegisterStudentModal: React.FC<RegisterStudentFormProps> = ({
  open,
  onClose,
  onSuccess,
  studentData,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<StudentFormData>({
    name: '',
    class: '',
    section: null,
    gender: '',
    email_id: '',
    admission_no: '',
    roll_no: '',
    dob: null,
    blood_group: '',
    father_name: '',
    mother_name: '',
    father_no: '',
    mother_no: '',
    address: '',
    user_login: '',
    user_pass: '',
    admission_type: null,
  });

  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { classOptions, fetchClasses, isFetching } = useClassesStore();

  // Current session comes from the already-populated school details store —
  // this modal doesn't own that fetch, it just reads whatever session is current.
  const schoolDetails = useSchoolDetailsStore((state) => state.schoolDetails);
  const currentSessionId = schoolDetails?.current_session?.id || null;

  const bloodGroupOptions: DropdownOption[] = [
    { value: '', label: 'Select Blood Group' },
    { value: 'A+', label: 'A+' },
    { value: 'A-', label: 'A-' },
    { value: 'B+', label: 'B+' },
    { value: 'B-', label: 'B-' },
    { value: 'AB+', label: 'AB+' },
    { value: 'AB-', label: 'AB-' },
    { value: 'O+', label: 'O+' },
    { value: 'O-', label: 'O-' },
    { value: 'Unknown', label: 'Unknown' },
  ];

  const genderOptions: RadioOption[] = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
  ];

  const admissionTypeOptions: RadioOption[] = [
    { value: 'Regular', label: 'Regular' },
    { value: 'Conditional', label: 'Conditional' },
  ];

  useEffect(() => {
    if (open) fetchClasses(true);
  }, [open, fetchClasses]);

  const selectedClass = classOptions.find((c) => c.id === formData.class);
  const selectedClassSections = selectedClass?.sections || [];
  const hasNoSections = selectedClass && (!selectedClass.sections || selectedClass.sections.length === 0);

  useEffect(() => {
    if (studentData && classOptions.length > 0) {
      const matchedClass = classOptions.find((c) => c.label === studentData.class_name);
      const classId = matchedClass?.id || '';
      setFormData({
        name: studentData.name || '',
        class: classId,
        section: matchedClass && matchedClass.sections?.includes(studentData.section) ? studentData.section : null,
        gender: studentData.gender || '',
        email_id: studentData.email_id || '',
        admission_no: studentData.admission_no || '',
        roll_no: studentData.roll_no?.toString() || '',
        dob: studentData.dob ? new Date(studentData.dob) : null,
        blood_group: studentData.blood_group || '',
        father_name: studentData.father_name || '',
        mother_name: studentData.mother_name || '',
        father_no: studentData.father_no || '',
        mother_no: studentData.mother_no || '',
        address: studentData.address || '',
        user_login: '',
        user_pass: '',
        admission_type: studentData.admission_type || null,
      });
      // in the useEffect that seeds the form from studentData
      const existingPictureUrl =
  studentData.profile_picture?.url || studentData.profile_picture_url || null;
setProfilePicturePreview(existingPictureUrl);
      setProfilePicture(null);
    } else if (!studentData && open) {
      resetForm();
    }
  }, [studentData, classOptions, open]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const resetForm = () => {
    setFormData({
      name: '',
      class: '',
      section: null,
      gender: '',
      email_id: '',
      admission_no: '',
      roll_no: '',
      dob: null,
      blood_group: '',
      father_name: '',
      mother_name: '',
      father_no: '',
      mother_no: '',
      address: '',
      user_login: '',
      user_pass: '',
      admission_type: null,
    });
    setProfilePicture(null);
    setProfilePicturePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.current?.show({
        severity: 'error',
        summary: 'Invalid File',
        detail: 'Only JPG, JPEG, PNG, and GIF files are allowed',
        life: 4000,
      });
      return;
    }

    if (file.size > 500 * 1024) {
      toast.current?.show({
        severity: 'error',
        summary: 'File Too Large',
        detail: 'Profile picture must be under 500KB',
        life: 4000,
      });
      return;
    }

    const imageInfo = new Image();
    imageInfo.src = URL.createObjectURL(file);

    setProfilePicture(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePicturePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  // handleRemoveProfilePicture
  const handleRemoveProfilePicture = () => {
  setProfilePicture(null);
  setProfilePicturePreview(
    studentData?.profile_picture?.url || studentData?.profile_picture_url || null
  );
  if (fileInputRef.current) fileInputRef.current.value = '';
};

  const getClassDropdownOptions = (): DropdownOption[] => {
    if (isFetching) return [{ value: '', label: 'Loading classes...' }];
    if (classOptions.length === 0) return [{ value: '', label: 'No classes available' }];
    return [{ value: '', label: 'Select Class' }, ...classOptions.map((c) => ({ value: c.id, label: c.label }))];
  };

  const getSectionOptions = (): DropdownOption[] => {
    if (!formData.class) return [{ value: '', label: 'Select Class First' }];
    if (isFetching) return [{ value: '', label: 'Loading...' }];
    if (!selectedClass) return [{ value: '', label: 'Class not found' }];
    if (hasNoSections) return [{ value: '', label: 'No Sections Available' }];
    return [
      { value: '', label: 'Select Section' },
      ...selectedClassSections.map((s) => ({ value: s, label: `Section ${s}` })),
    ];
  };

  const getRequiredFields = () => {
    const baseFields = studentData
      ? ['name', 'class', 'gender', 'email_id', 'admission_no', 'roll_no', 'dob', 'father_name', 'mother_name', 'father_no', 'address']
      : ['name', 'class', 'gender', 'email_id', 'admission_no', 'roll_no', 'dob', 'father_name', 'mother_name', 'father_no', 'address', 'user_login', 'user_pass'];

    if (!hasNoSections && selectedClassSections.length > 0) {
      baseFields.push('section');
    }

    return baseFields;
  };

  const handleSubmit = async () => {
    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter((field) => {
      const value = formData[field as keyof StudentFormData];
      if (field === 'dob') return !value;
      return !value?.toString().trim();
    });

    if (missingFields.length > 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: `Missing required fields: ${missingFields.join(', ')}`,
        life: 5000,
      });
      return;
    }

    // Both registration and update touch class/section/roll_no, which live
    // on StudentSession — both need a session_id, sourced the same way
    // from useSchoolDetailsStore.
    if (!currentSessionId) {
      toast.current?.show({
        severity: 'error',
        summary: 'No Active Session',
        detail: 'Could not determine the current session for this school. Please refresh and try again.',
        life: 5000,
      });
      return;
    }

    setLoading(true);
    try {
      const dobFormatted = formData.dob
        ? [
            formData.dob.getFullYear(),
            String(formData.dob.getMonth() + 1).padStart(2, '0'),
            String(formData.dob.getDate()).padStart(2, '0'),
          ].join('-')
        : '';

      let response;

      if (studentData) {
        // ── Update flow — wired to the new PUT /students/:id endpoint ──
        // class_id/section/roll_no live on StudentSession, so session_id
        // must go along with them — using the school's current session,
        // same source the rest of this form/dashboard uses.
        const fd = new FormData();
        fd.append('name', formData.name);
        fd.append('class_id', formData.class);
        fd.append('section', hasNoSections ? '' : (formData.section || ''));
        if (currentSessionId) {
          fd.append('session_id', currentSessionId);
        }
        fd.append('roll_no', formData.roll_no);
        fd.append('gender', formData.gender);
        fd.append('email_id', formData.email_id);
        fd.append('admission_no', formData.admission_no);
        fd.append('dob', dobFormatted);
        fd.append('blood_group', formData.blood_group || '');
        fd.append('admission_type', formData.admission_type || '');
        fd.append('father_name', formData.father_name);
        fd.append('mother_name', formData.mother_name);
        fd.append('father_no', formData.father_no);
        fd.append('mother_no', formData.mother_no || '');
        fd.append('address', formData.address);
        if (profilePicture) {
          fd.append('profile_picture', profilePicture);
        }

        response = await apiClient({
          method: 'PUT',
          url: `students/${studentData.id}`,
          data: fd,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // ── Register flow — wired to the new Go /students endpoint ──
        // school_id is deliberately omitted: the backend resolves it from
        // the JWT context for every role except superadmin targeting
        // another school, which this form doesn't need to support.
        response = await apiClient({
          method: 'POST',
          url: 'students',
          data: {
            session_id: currentSessionId,
            class_id: formData.class,
            section: hasNoSections ? null : formData.section,
            name: formData.name,
            gender: formData.gender,
            email_id: formData.email_id,
            admission_no: formData.admission_no,
            roll_no: Number(formData.roll_no),
            dob: dobFormatted,
            blood_group: formData.blood_group || undefined,
            admission_type: formData.admission_type || undefined,
            father_name: formData.father_name,
            mother_name: formData.mother_name,
            father_no: formData.father_no,
            mother_no: formData.mother_no || undefined,
            address: formData.address,
            user_login: formData.user_login,
            user_pass: formData.user_pass,
          },
        });
      }

      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: studentData ? 'Student updated successfully!' : 'Student registered successfully!',
          life: 4000,
        });
        setTimeout(() => {
          resetForm();
          onSuccess(!studentData);
          handleClose();
        }, 300);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || `Failed to ${studentData ? 'update' : 'register'} student`,
          life: 5000,
        });
      }
    } catch (err: any) {
      const message = err.response?.data?.message || `Failed to ${studentData ? 'update' : 'register'} student`;
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: message,
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div
        style={{
          width: '54px',
          height: '54px',
          borderRadius: '14px',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '24px',
          fontWeight: 700,
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
          flexShrink: 0,
          border: '3px solid white',
        }}
      >
        <i className={studentData ? 'pi pi-user-edit' : 'pi pi-user-plus'}></i>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
          {studentData ? 'Edit Student Information' : 'Register New Student'}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          {studentData ? 'Update student details below' : 'Fill in the student information below'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '800px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          .p-dialog-mask.mask-closing {
            animation: maskFadeOut 0.4s ease !important;
          }
          @keyframes maskFadeIn {
            from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
            to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          }
          @keyframes maskFadeOut {
            from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
            to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          }
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          .p-dialog.dialog-closing {
            animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          @keyframes dialogOpen {
            from { opacity: 0; transform: scale(0.92) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes dialogClose {
            from { opacity: 1; transform: scale(1) translateY(0); }
            to { opacity: 0; transform: scale(0.88) translateY(25px); }
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241, 245, 249, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100, 116, 139, 0.7); }
          .profile-upload-area:hover { border-color: #3b82f6 !important; background: rgba(59, 130, 246, 0.05) !important; }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Profile Picture Section — only shown when editing */}
          {studentData && (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '16px',
                padding: '20px',
                border: '1px solid rgba(59, 130, 246, 0.1)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '16px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                    color: 'white',
                  }}
                >
                  <i className="pi pi-camera" style={{ fontSize: '15px' }}></i>
                </div>
                <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                  Profile Picture
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div
                  style={{
                    width: '90px',
                    height: '90px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid rgba(59, 130, 246, 0.3)',
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {profilePicturePreview ? (
                    <img
                      src={profilePicturePreview}
                      alt="Profile"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <i className="pi pi-user" style={{ fontSize: '32px', color: '#6366f1' }}></i>
                  )}
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    onChange={handleProfilePictureChange}
                    style={{ display: 'none' }}
                  />

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1.5px solid #3b82f6',
                        background: 'white',
                        color: '#3b82f6',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        opacity: loading ? 0.6 : 1,
                      }}
                    >
                      <i className="pi pi-upload" style={{ fontSize: '12px' }}></i>
                      {profilePicturePreview ? 'Change Photo' : 'Upload Photo'}
                    </button>

                    {profilePicture && (
                      <button
                        onClick={handleRemoveProfilePicture}
                        disabled={loading}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1.5px solid #ef4444',
                          background: 'white',
                          color: '#ef4444',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: loading ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          opacity: loading ? 0.6 : 1,
                        }}
                      >
                        <i className="pi pi-times" style={{ fontSize: '12px' }}></i>
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
                    {profilePicture ? (
                      <span style={{ color: '#10b981', fontWeight: 600 }}>
                        <i className="pi pi-check-circle" style={{ marginRight: '4px' }}></i>
                        {profilePicture.name} ({(profilePicture.size / 1024).toFixed(1)} KB)
                      </span>
                    ) : (
                      'JPG, PNG or GIF · Max 500KB'
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Personal Information Section */}
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '16px',
              padding: '20px',
              border: '1px solid rgba(59, 130, 246, 0.1)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: 'white',
                }}
              >
                <i className="pi pi-user" style={{ fontSize: '15px' }}></i>
              </div>
              <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                Personal Information
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <GlobalInputField
                label="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter student's full name"
                required
                disabled={loading}
                icon="pi pi-user"
              />

              {!studentData && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <GlobalInputField
                    label="Username"
                    value={formData.user_login}
                    onChange={(e) => setFormData({ ...formData, user_login: e.target.value })}
                    placeholder="Enter username"
                    required
                    disabled={loading}
                    icon="pi pi-at"
                  />
                  <GlobalInputField
                    label="Password"
                    type="password"
                    value={formData.user_pass}
                    onChange={(e) => setFormData({ ...formData, user_pass: e.target.value })}
                    placeholder="Enter password"
                    required
                    disabled={loading}
                    icon="pi pi-lock"
                  />
                </div>
              )}

              <GlobalRadioButtons
                label="Gender"
                options={genderOptions}
                value={formData.gender}
                onChange={(value) => setFormData({ ...formData, gender: value })}
                required
                disabled={loading}
                layout="horizontal"
              />
            </div>
          </div>

          {/* Academic Information Section */}
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '16px',
              padding: '20px',
              border: '1px solid rgba(59, 130, 246, 0.1)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: 'white',
                }}
              >
                <i className="pi pi-book" style={{ fontSize: '15px' }}></i>
              </div>
              <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                Academic Information
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: !hasNoSections && selectedClassSections.length > 0 ? '1fr 1fr' : '1fr', gap: '16px' }}>
                <GlobalDropdownField
                  label="Class"
                  value={formData.class}
                  onChange={(value) => setFormData({ ...formData, class: value, section: null })}
                  options={getClassDropdownOptions()}
                  placeholder="Select class"
                  required
                  disabled={isFetching || loading}
                  icon="pi pi-users"
                />

                {!hasNoSections && selectedClassSections.length > 0 && (
                  <div>
                    <GlobalDropdownField
                      label="Section"
                      value={formData.section || ''}
                      onChange={(value) => setFormData({ ...formData, section: value })}
                      options={getSectionOptions()}
                      placeholder="Select section"
                      required
                      disabled={!formData.class || isFetching || loading}
                      icon="pi pi-list"
                    />
                    {formData.class && (
                      <div style={{ marginTop: '6px', fontSize: '11px', color: '#10b981', fontWeight: 500 }}>
                        Available: {selectedClassSections.join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {hasNoSections && formData.class && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    fontSize: '12px',
                    color: '#dc2626',
                    fontWeight: 500,
                  }}
                >
                  <i className="pi pi-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                  This class has no sections configured
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <GlobalInputField
                  label="Admission Number"
                  value={formData.admission_no}
                  onChange={(e) => setFormData({ ...formData, admission_no: e.target.value })}
                  placeholder="Enter admission no."
                  required
                  disabled={loading}
                  icon="pi pi-id-card"
                />
                <GlobalInputField
                  label="Roll Number"
                  type="number"
                  value={formData.roll_no}
                  onChange={(e) => setFormData({ ...formData, roll_no: e.target.value })}
                  placeholder="Enter roll no."
                  required
                  disabled={loading}
                  icon="pi pi-hashtag"
                />
              </div>

              {/* ── Admission Type (optional) ── */}
              <div>
                <GlobalRadioButtons
                  label="Admission Type"
                  options={admissionTypeOptions}
                  value={formData.admission_type || ''}
                  onChange={(value) => setFormData({ ...formData, admission_type: value || null })}
                  disabled={loading}
                  layout="horizontal"
                />
                {/* Clear button — lets user reset to NULL */}
                {formData.admission_type && (
                  <button
                    onClick={() => setFormData({ ...formData, admission_type: null })}
                    disabled={loading}
                    style={{
                      marginTop: '6px',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      background: 'transparent',
                      color: '#64748b',
                      fontSize: '12px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <i className="pi pi-times" style={{ fontSize: '10px' }}></i>
                    Clear
                  </button>
                )}
              </div>

            </div>
          </div>

          {/* Contact & Additional Information Section */}
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '16px',
              padding: '20px',
              border: '1px solid rgba(59, 130, 246, 0.1)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: 'white',
                }}
              >
                <i className="pi pi-info-circle" style={{ fontSize: '15px' }}></i>
              </div>
              <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                Contact & Additional Information
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <GlobalInputField
                  label="Email"
                  type="email"
                  value={formData.email_id}
                  onChange={(e) => setFormData({ ...formData, email_id: e.target.value })}
                  placeholder="student@example.com"
                  required
                  disabled={loading}
                  icon="pi pi-envelope"
                />
                <GlobalDateField
                  label="Date of Birth"
                  value={formData.dob}
                  onChange={(date) => setFormData({ ...formData, dob: date as Date | null })}
                  placeholder="Select date"
                  required
                  disabled={loading}
                  dateFormat="dd/mm/yy"
                  showButtonBar
                  maxDate={new Date()}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <GlobalDropdownField
                  label="Blood Group"
                  value={formData.blood_group || ''}
                  onChange={(value) => setFormData({ ...formData, blood_group: value })}
                  options={bloodGroupOptions}
                  placeholder="Select blood group"
                  disabled={loading}
                  icon="pi pi-heart"
                />
                <GlobalInputField
                  label="Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter address"
                  required
                  disabled={loading}
                  icon="pi pi-map-marker"
                />
              </div>
            </div>
          </div>

          {/* Parent Information Section */}
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '16px',
              padding: '20px',
              border: '1px solid rgba(59, 130, 246, 0.1)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: 'white',
                }}
              >
                <i className="pi pi-users" style={{ fontSize: '15px' }}></i>
              </div>
              <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                Parent Information
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <GlobalInputField
                  label="Father's Name"
                  value={formData.father_name}
                  onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                  placeholder="Enter father's name"
                  required
                  disabled={loading}
                  icon="pi pi-user"
                />
                <GlobalInputField
                  label="Mother's Name"
                  value={formData.mother_name}
                  onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })}
                  placeholder="Enter mother's name"
                  required
                  disabled={loading}
                  icon="pi pi-user"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <GlobalInputField
                  label="Father's Contact"
                  type="tel"
                  value={formData.father_no}
                  onChange={(e) => setFormData({ ...formData, father_no: e.target.value })}
                  placeholder="Enter contact number"
                  required
                  disabled={loading}
                  icon="pi pi-phone"
                />
                <GlobalInputField
                  label="Mother's Contact"
                  type="tel"
                  value={formData.mother_no}
                  onChange={(e) => setFormData({ ...formData, mother_no: e.target.value })}
                  placeholder="Enter contact number"
                  disabled={loading}
                  icon="pi pi-phone"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <GlobalButton
              label={
                loading
                  ? studentData ? 'Updating...' : 'Registering...'
                  : studentData ? 'Update Student' : 'Register Student'
              }
              onClick={handleSubmit}
              variant="primary"
              size="medium"
              disabled={loading || isFetching}
              loading={loading}
              fullWidth={false}
            />
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default RegisterStudentModal;